import React, { FC, useState } from 'react';
import { Popover } from '@mantine/core';
import { LoadingSkeleton } from 'components/LoadingSkeleton';
import { Nothing } from 'components/Nothing';
import {
  useGetEmployeesListQuery,
  useDeleteEmployeeMutation,
} from 'redux/Api/employeesApi/employeesApi';
import { ReactComponent as IconKebab } from './img/iconKebab.svg';
import { ReactComponent as IconClose } from './img/iconClose.svg';
import { ReactComponent as IconCancel } from './img/iconCancel.svg';
import { ReactComponent as IconDanger } from './img/iconDanger.svg';
import { ReactComponent as IconDone } from './img/iconDone.svg';
import { ReactComponent as IconDismissed } from './img/iconDismissed.svg';
import styles from './Employees.module.scss';
import { TextWithTooltip } from 'hooks/TextWithTooltip';
import { useMediaQuery } from '@mantine/hooks';

interface EmployeesTypes {
  id?: number | null;
  title?: string;
  openNav?: boolean | undefined;
}

interface EmployeeActionsProps {
  employee: any;
  onDelete: (id: number) => void;
  isDeleting: boolean;
  isMediumScreen?: boolean;
}

const EmployeeActions: FC<EmployeeActionsProps> = ({
  employee,
  onDelete,
  isMediumScreen,
}) => {
  const [opened, setOpened] = useState(false);

  const handleDelete = () => {
    onDelete(employee.user_id);
    setOpened(false);
  };

  return (
    <Popover
      width={'fit-content'}
      position='bottom-end'
      radius={'4px'}
      opened={opened}
      onChange={setOpened}>
      <Popover.Target>
        <IconKebab
          className={isMediumScreen ? styles.kebabIconMedium : styles.kebabIcon}
          onClick={() => setOpened((prevOpened: boolean) => !prevOpened)}
        />
      </Popover.Target>
      <Popover.Dropdown
        p='4px 0'
        style={{
          borderRadius: '4px',
          boxShadow: '0px 0px 10px 0px rgba(0, 32, 51, 0.10)',
          border: 'none',
        }}>
        <div className={styles.menuItem} onClick={handleDelete}>
          <IconClose style={{ height: '24px', width: '24px' }} />
          <span>Удалить представителя</span>
        </div>
      </Popover.Dropdown>
    </Popover>
  );
};

export const Employees: FC<EmployeesTypes> = ({ openNav }) => {
  const { data: employees, isLoading } = useGetEmployeesListQuery();
  const [deleteEmployee, { isLoading: isDeleting }] = useDeleteEmployeeMutation();
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const isMobile = useMediaQuery(`(width < 768px)`);
  const isMediumScreen = useMediaQuery('(width < 1901px)');
  const isLessThan1595px = useMediaQuery('(width < 1595px)');
  const isLessThan1390px = useMediaQuery('(width < 1400px)');
  const showFullTable = !((!openNav && isLessThan1595px) || isLessThan1390px);

  const formatPhone = (phone: string) => {
    if (!phone) return 'Не указан';

    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 11 && cleaned.startsWith('7')) {
      return `+7 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(
        7,
        9,
      )}-${cleaned.slice(9)}`;
    }
    return phone;
  };

  const getRoleName = (position: string) => {
    if (position === 'employee') return 'Сотрудник';
    else if (position === 'director') return 'Администратор';
    else return 'Не указана';
  };

  const getAccessName = (can_manage_orders: boolean) => {
    return can_manage_orders ? 'Право подписи' : 'Без права подписи';
  };

  const getStatusConfig = (status: 'fired' | 'new' | 'accepted' | 'rejected') => {
    const config: Record<
      'fired' | 'new' | 'accepted' | 'rejected',
      { name: string; color: string; icon: React.ReactNode }
    > = {
      fired: {
        name: 'Уволен',
        color: '#E10D0D',
        icon: <IconDismissed style={{ width: 20, height: 20 }} />,
      },
      new: {
        name: 'Ожидает подтверждения',
        color: '#F89500',
        icon: <IconDanger style={{ width: 20, height: 20 }} />,
      },
      accepted: {
        name: 'Активен',
        color: '#28A879',
        icon: <IconDone style={{ width: 20, height: 20 }} />,
      },
      rejected: {
        name: 'Приглашение отклонено',
        color: 'black',
        icon: <IconCancel style={{ width: 20, height: 20 }} />,
      },
    };

    return config[status];
  };

  const getStatusName = (status: 'fired' | 'new' | 'accepted' | 'rejected') => {
    const { name, color, icon } = getStatusConfig(status);

    return (
      <span className={styles.text_status}>
        <span className={styles.status_icon}>{icon}</span>
        <span style={{ color, fontSize: '16px' }}>{name}</span>
      </span>
    );
  };

  const renderStatusCell = (status: 'fired' | 'new' | 'accepted' | 'rejected') => {
    const { name, color, icon } = getStatusConfig(status);

    return (
      <span className={styles.text_status}>
        <span className={styles.status_icon}>{icon}</span>
        <TextWithTooltip content={name} style={{ color, fontSize: '16px' }} />
      </span>
    );
  };

  const rubFormatter = new Intl.NumberFormat('ru-RU', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  const getLimitString = (
    ordersSumThisMonth: number | string | null,
    limit: number | null,
  ): JSX.Element | string => {
    if (ordersSumThisMonth == null && limit == null) return '';
    let formattedOrdersSum;
    let formattedLimit;
    const ordersSum = ordersSumThisMonth != null ? Number(ordersSumThisMonth) : NaN;
    if (!Number.isNaN(ordersSum) && ordersSum !== 0) {
      formattedOrdersSum = rubFormatter.format(ordersSum);
    }
    if (limit !== null) {
      formattedLimit = rubFormatter.format(limit);
    }

    return (
      <>
        {formattedOrdersSum ? `${formattedOrdersSum} из ` : ''}
        {formattedLimit ? formattedLimit : ''}
      </>
    );
  };

  const handleDelete = async (employeeId: number) => {
    setDeletingId(employeeId);
    try {
      await deleteEmployee({ employeeId }).unwrap();
    } catch (error) {
      console.error('Ошибка удаления сотрудника:', error);
    } finally {
      setDeletingId(null);
    }
  };

  if (isLoading) {
    return (
      <div className={styles.progressBlock}>
        <LoadingSkeleton fullWidth={true} />
      </div>
    );
  }

  return (
    <div>
      {showFullTable ? (
        <div className={styles.table}>
          <div className={styles.table_title}>
            <p>Представитель</p>
            <p>Телефон</p>
            <p>Роль</p>
            <p>Статус</p>
            <p style={{ textAlign: 'end', paddingRight: '12px' }}>Лимит, ₽</p>
            <p>Доступ</p>
            <p style={{ textAlign: 'end' }}>Действия</p>
          </div>
          {employees && employees.length > 0 ? (
            employees.map((employee: any) => (
              <div className={styles.table_row} key={employee.id}>
                <TextWithTooltip content={employee.fio} />
                <TextWithTooltip content={formatPhone(employee.phone)} />
                <TextWithTooltip content={getRoleName(employee?.position || '')} />
                {renderStatusCell(employee.status)}
                <TextWithTooltip
                  content={getLimitString(
                    employee.orders_sum_this_month,
                    employee.orders_limit,
                  )}
                  style={{ textAlign: 'end', paddingRight: '12px' }}
                />
                <TextWithTooltip content={getAccessName(employee.can_manage_orders)} />
                {employee?.position === 'employee' && employee?.status === 'accepted' && (
                  <div className={styles.table_row_action}>
                    <EmployeeActions
                      employee={employee}
                      onDelete={handleDelete}
                      isDeleting={isDeleting && deletingId === employee.id}
                    />
                  </div>
                )}
              </div>
            ))
          ) : (
            <Nothing style={{ height: '200px' }} title='Сотрудники не найдены' />
          )}
        </div>
      ) : (
        <div>
          {employees && employees.length > 0 ? (
            employees.map((employee: any) => (
              <div key={employee.id} className={styles.mobile_row}>
                <div className={styles.mobile_content}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <div>
                      <div className={styles.name}>
                        <TextWithTooltip
                          content={employee.fio}
                          className={styles.mobile_row_name}
                          isMobile={true}
                        />
                        <p>{formatPhone(employee.phone)}</p>
                      </div>
                    </div>
                    {employee?.position === 'employee' &&
                      employee?.status === 'accepted' && (
                        <EmployeeActions
                          employee={employee}
                          onDelete={handleDelete}
                          isDeleting={isDeleting && deletingId === employee.id}
                          isMediumScreen={isMediumScreen}
                        />
                      )}
                  </div>

                  <div className={styles.mobile_details}>
                    <div
                      className={
                        isMobile ? styles.line_content_mobile : styles.line_content
                      }>
                      <p>Роль</p>
                      <span style={{ color: 'black', fontSize: '16px' }}>
                        {getRoleName(employee?.position)}
                      </span>
                    </div>

                    <div
                      className={
                        isMobile ? styles.line_content_mobile : styles.line_content
                      }>
                      <p>Статус</p>
                      {getStatusName(employee.status)}
                    </div>

                    <div
                      className={
                        isMobile ? styles.line_content_mobile : styles.line_content
                      }>
                      <p>Лимит, ₽</p>
                      <span style={{ color: 'black', fontSize: '16px' }}>
                        {getLimitString(
                          employee.orders_sum_this_month,
                          employee.orders_limit,
                        )}
                      </span>
                    </div>

                    <div
                      className={
                        isMobile ? styles.line_content_mobile : styles.line_content
                      }>
                      <p>Доступ</p>
                      <span style={{ color: 'black', fontSize: '16px' }}>
                        {getAccessName(employee.can_manage_orders)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <Nothing style={{ height: '200px' }} title='Сотрудники не найдены' />
          )}
        </div>
      )}
    </div>
  );
};
